import type { ElementNodeDto } from './schemas/element.schema';
import { zoneOf } from './pageZone';

// Which pictures a page loads at once instead of lazily: the header's (its logo) and the first picture of its main
// content — the hero a page's largest paint is almost always made of. Shared, so the storefront that draws the page and
// the server that preloads its hero in the HTML pick the same picture.

/** The ids of the pictures to load at once, and the one among them to fetch first. */
export interface EagerImages {
  eager: ReadonlySet<string>;
  lead: string | null;
}

// Finds the first picture with a file under a node, in the order the page shows them.
function firstImage(node: ElementNodeDto): ElementNodeDto | null {
  if (node.type === 'image' && typeof node.props.src === 'string' && node.props.src) return node;
  for (const child of node.children) {
    const found = firstImage(child);
    if (found) return found;
  }
  return null;
}

// Lists every picture with a file under a node.
function allImages(node: ElementNodeDto, into: string[]): string[] {
  if (node.type === 'image' && node.props.src) into.push(node.id);
  for (const child of node.children) allImages(child, into);
  return into;
}

// Finds a page's lead picture: the first one of its main content, or null when it has none.
export function leadImageNode(roots: readonly ElementNodeDto[]): ElementNodeDto | null {
  return roots.filter((root) => zoneOf(root) === 'template').map(firstImage).find(Boolean) ?? null;
}

// Picks a page's pictures to load at once: the header's, and the lead one, fetched first.
export function eagerImages(roots: readonly ElementNodeDto[]): EagerImages {
  const header = roots.filter((root) => zoneOf(root) === 'header').flatMap((root) => allImages(root, []));
  const lead = leadImageNode(roots);
  return { eager: new Set([...header, ...(lead ? [lead.id] : [])]), lead: lead?.id ?? null };
}
